import {
  Autocomplete,
  Typography,
  Box,
  Icon,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import React from "react";
import { cloneDeep } from "lodash";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch } from "react-redux";
import { getStockProducts } from "../../../api/stockProducts";
import Panel from "../../../components/Panel";
import { t, tl } from "../../../components/translate";
import { Medicine } from "../../../interfaces/AssessmentInterfaces";
import { ProductInterface } from "../../../interfaces/ProductInterface";
import CreateStockProduct from "../../Stock/StockCreateEdit/CreateStockProduct";
import MedicationDialog from "../Medication/MedicationDialog";
import { countableTypes, medicineType } from "../Medication/medicineType";
import * as NotificationActions from "../../../actions/notification";
import {
  getMedicineMultiplier,
  showTimeUnit,
  showFrequencyUnit,
  showMeal
} from "../Medication/Medication";
import DebouncedTextField from "../../../components/DebouncedTextField";
import useAssessmentLabel from "../../../hooks/useAssessmentLabel";

// @ts-expect-error - css
import styles from "../Assessment.module.css";

interface ProductNewInterface extends ProductInterface {
  inputValue?: string;
}

interface ProductSelectProps {
  productMedication: Medicine[];
  onChange: (productMedication: Medicine[]) => void;
}

const emptyMedicineData: Medicine = {
  brand: "",
  duration: 0,
  form: "",
  frequency: 0,
  frequencyType: "",
  genericName: "",
  id: null,
  meal: "",
  remark: "",
  strength: "",
  times: ""
};

export default function ProductMedication({
  productMedication,
  onChange
}: ProductSelectProps): React.JSX.Element {
  // hooks
  const dispatch = useDispatch();
  const assessmentLabels = useAssessmentLabel();

  // local state
  const [productName, setProductName] = React.useState<string>("");
  const [showCreatePanel, setShowCreatePanel] = React.useState<boolean>(false);
  const [stockProducts, setStockProducts] = React.useState<ProductNewInterface[]>([]);
  const [selectedProduct, setSelectedProduct] = React.useState<ProductInterface>(null);
  const [openMedicationDialog, setOpenMedicationDialog] = React.useState<boolean>(false);
  const [editingIndex, setEditingIndex] = React.useState<number>(-1);
  const [editingMedicineData, setEditingMedicineData] = React.useState<Medicine>(emptyMedicineData);

  // helper functions
  const textChangeHandler = async ({ target }) => {
    try {
      const query = (target.value || "").trim();
      if (query.length > 2) {
        const productList = (await getStockProducts({
          search: query,
          transactionType: 1,
          limit: 20,
          intangible: false,
          excludeStockAndRates: true
        })) as ProductInterface[];
        if (productList?.length) {
          setStockProducts(productList as ProductNewInterface[]);
        } else {
          setStockProducts([]);
        }
      }
    } catch (error) {
      dispatch(
        NotificationActions.notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: "Sorry !, something went wrong while getting products.",
          autoTimeout: true
        })
      );
    }
  };

  const handleModalSave = (data) => {
    if (data) {
      const updated = cloneDeep(productMedication);
      if (editingIndex >= 0) {
        updated.splice(editingIndex, 1, data);
      } else {
        updated.push(data);
      }
      onChange(updated);
      setOpenMedicationDialog(false);
      setSelectedProduct(null);
      setStockProducts([]);
      setEditingIndex(-1);
      setEditingMedicineData(emptyMedicineData);
    }
  };

  return (
    <>
      <Box className={styles.assessmentRow}>
        <Box className={styles.assessmentLabel}>
          <Typography component="span" fontWeight="600">
            {assessmentLabels.productMedication}
          </Typography>
        </Box>
        <Box className={styles.assessmentField}>
          <Autocomplete
            options={stockProducts}
            clearOnBlur
            fullWidth
            freeSolo
            value={selectedProduct || null}
            onChange={(_, val) => {
              if (typeof val === "string") {
                return;
              }
              if (val?.inputValue) {
                setShowCreatePanel(true);
                setProductName(val.inputValue);
              } else if (val) {
                setOpenMedicationDialog(true);
                setSelectedProduct(val);
              }
            }}
            filterOptions={(options, params) => {
              if (params.inputValue !== "") {
                options.push({
                  inputValue: params.inputValue,
                  name: `Add "${params.inputValue}"`
                } as ProductNewInterface);
              }
              return options;
            }}
            renderInput={(params) => (
              <DebouncedTextField
                debounceAt={1000}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                className="stock_item_autocomplete"
                placeholder="Select Medication Products"
                variant="outlined"
                margin="dense"
                slotProps={{
                  inputLabel: { shrink: true }
                }}
                onFocus={(e) => e.target.select()}
                onChange={textChangeHandler}
              />
            )}
            renderOption={(ownProps, option) => (
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
              <li
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...ownProps}
                key={option.id || "customFilterKey"}
                style={{
                  display: "flex",
                  justifyContent: "space-between"
                }}
                onClick={(e) => {
                  ownProps.onClick(e);
                  e.stopPropagation();
                }}
                onKeyDown={null}
              >
                <Typography style={{ width: "80%" }}>{option?.name}</Typography>
              </li>
            )}
            getOptionLabel={(option) => option.name}
          />
        </Box>
      </Box>

      <MedicationDialog
        handleCancel={() => {
          setOpenMedicationDialog(false);
          setStockProducts([]);
          setEditingIndex(-1);
          setEditingMedicineData(emptyMedicineData);
        }}
        handleModalSave={handleModalSave}
        modalIsOpen={openMedicationDialog}
        data={
          editingIndex >= 0
            ? editingMedicineData
            : {
                ...emptyMedicineData,
                brand: selectedProduct?.name,
                id: selectedProduct?.id,
                strength: selectedProduct?.structuredInfo?.strength || "",
                form: selectedProduct?.structuredInfo?.form || "",
                genericName: selectedProduct?.structuredInfo?.genericName || ""
              }
        }
        isProductMedication
      />

      {showCreatePanel && (
        <Panel onClose={() => setShowCreatePanel(false)} title="Create Product">
          <CreateStockProduct
            productName={productName}
            handleClose={() => setShowCreatePanel(false)}
            saveAction={(savedItem) => {
              if (savedItem) {
                setOpenMedicationDialog(true);
                setSelectedProduct(savedItem);
              }
            }}
            mode="Create"
          />
        </Panel>
      )}

      <>
        {productMedication && productMedication.length > 0 && (
          <TableContainer
            sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", overflowX: "auto", mt: 1 }}
          >
            <Table size="small" sx={{ minWidth: 650 }}>
              <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 50 }}>S.N.</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Medicine</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Frequency / Instruction</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Duration &amp; Form</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 80, textAlign: "center" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productMedication.map((item, index) => (
                  <TableRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${item?.id}-${index}`}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell sx={{ verticalAlign: "top" }}>{index + 1}.</TableCell>

                    <TableCell sx={{ verticalAlign: "top" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Icon style={{ color: "black", fontSize: "1.2rem" }}>
                          {item.form ? medicineType[item.form]() : medicineType.Tablet()}
                        </Icon>
                        <Box>
                          <Box fontSize="0.95rem" fontWeight="600">
                            {`${item.brand} ${item.strength}`}
                          </Box>
                          <Box fontSize="0.85rem" color="text.secondary">
                            {item.genericName}
                          </Box>
                          {item.remark && (
                            <Box fontSize="0.8rem" sx={{ mt: 0.5 }}>
                              <b>{tl("assessment.medication.remark")}</b>: {item.remark}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ verticalAlign: "top" }}>
                      <Box>
                        {item.frequency === 0
                          ? ""
                          : `${item.frequency || ""} ${t("assessment.times")}`}
                        {item.frequency !== 0 && showTimeUnit(item.times)}
                        {item.meal && ` - ${showMeal(item.meal)}`}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ verticalAlign: "top" }}>
                      {item.frequency !== 0 && (
                        <>
                          {countableTypes.includes(item.form)
                            ? `${
                                (item.frequency || 0) *
                                getMedicineMultiplier(
                                  item.times,
                                  item.duration || 0,
                                  item.frequencyType
                                )
                              } `
                            : ""}
                          {`${item.form} (${item.duration || 0} ${showFrequencyUnit(
                            item.frequencyType,
                            item.duration
                          )})`}
                        </>
                      )}
                    </TableCell>

                    <TableCell
                      sx={{ verticalAlign: "middle", textAlign: "center", whiteSpace: "nowrap" }}
                    >
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          setEditingIndex(index);
                          setEditingMedicineData(item);
                          setOpenMedicationDialog(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          const updated = cloneDeep(productMedication);
                          updated.splice(index, 1);
                          onChange(updated);
                        }}
                      >
                        <HighlightOffIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </>
    </>
  );
}
